import React, { ReactElement, ReactNode, useEffect, useState } from 'react'

import { Portal } from 'conversations/components/Portal'
import { usePopper } from 'react-popper'
import { CSSTransition } from 'react-transition-group'
import { useDebouncedCallback } from 'use-debounce'

import './animationStyles.css'
import styles from './styles.module.css'

interface FadeInProps {
  children: ReactElement | ReactNode
  inProp: boolean
  delay?: number
}

const FadeInAnimation = ({ children, delay = 300, inProp }: FadeInProps) => {
  return (
    <CSSTransition unMountOnExit in={inProp} timeout={delay} classNames="fadeAnimation" appear>
      {children}
    </CSSTransition>
  )
}

interface PortalTooltipProps {
  children: React.ReactElement
  tooltipBody: React.ReactElement
  refElement: HTMLDivElement | null
  boundaryRef: HTMLDivElement | null
  placement?: any
  className?: string
  trackingEventName?: string
  isAlwaysOpen?: boolean
}

const PortalTooltip = ({
  trackingEventName,
  placement = 'left-end',
  refElement,
  boundaryRef,
  tooltipBody,
  className,
  isAlwaysOpen = false,
  children,
}: PortalTooltipProps) => {
  const [isShown, setIsShown] = useState(isAlwaysOpen)

  const debouncedMouseEvent = useDebouncedCallback((toggle: boolean) => {
    !isAlwaysOpen && setIsShown(toggle)
  }, 100)

  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null)
  const [isHidden, setIsHidden] = useState<boolean>(true)
  const {
    styles: popperStyles,
    attributes,
    forceUpdate,
  } = usePopper(refElement, popperElement, {
    placement,
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [200, 20],
        },
      },
      {
        name: 'preventOverflow',
        options: {
          rootBoundary: 'document',
          boundary: boundaryRef ?? 'clippingParents',
          mainAxis: true,
          altAxis: false,
          padding: 0,
          altBoundary: true,
          tether: false,
        },
      },
    ],
  })

  useEffect(() => {
    let timer: NodeJS.Timeout | undefined
    if (forceUpdate && isHidden) {
      timer = setTimeout(() => {
        setIsHidden(false)
        forceUpdate()
      }, 1000)
    }
    return () => {
      if (timer) clearTimeout(timer)
    }
  }, [forceUpdate, isHidden])

  return (
    <div
      className={className}
      data-ocweb-id={trackingEventName}
      onMouseEnter={() => {
        setIsShown(true)
        debouncedMouseEvent(true)
      }}
      onMouseLeave={() => debouncedMouseEvent(false)}>
      {children}
      {isShown && (
        <FadeInAnimation delay={700} inProp={isShown}>
          <Portal>
            <div
              className={isHidden ? styles.hiddenPopper : styles.popper}
              ref={setPopperElement}
              style={popperStyles.popper}
              {...attributes.popper}>
              {tooltipBody}
            </div>
          </Portal>
        </FadeInAnimation>
      )}
    </div>
  )
}

export default PortalTooltip
